import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { Feature } from 'geojson';
import { SearchFieldComponent } from 'src/app/history-analysis/search-field/search-field.component';
import embed, { Mode, VisualizationSpec } from 'vega-embed';
import { Alerta } from '../models/alerta';
import { Tempo } from '../models/tempo';
import { TimespanService } from '../services/timespan/timespan.service';

/**
 * Este componente exibe um gráfico da mudança das intensidades de chuva registradas \
 * pelas estações no intervalo de tempo do alerta / aviso.
 */
@Component({
  selector: 'app-alert-matrix',
  templateUrl: './alert-matrix.component.html',
  styleUrls: ['./alert-matrix.component.css']
})
export class AlertMatrixComponent implements OnInit {

  stationsArray: any[] = [];
  selectedStations: string[] = [];
  chart: boolean = false;
  tempo :Tempo;
  stationName: string = "";
  alerta: Alerta;
  RT: boolean = false;
  props: any[] = [];

  @Output() tableClosed = new EventEmitter<boolean>();

  @Output() searchDate = new EventEmitter<any>();

  @Output() dragDisabled = new EventEmitter<boolean>();
  
  @ViewChild('searchField') searchField: SearchFieldComponent;

  heatmatrixClass = "alertMatrix-hide";

  discreteColors: {[key: string]: string} = {
    'fraca':'#339944', 
    'moderada':'#FFCC00', 
    'forte': '#FF8822', 
    'muito forte':'#DD2233'
  };

  constructor(private timespanService: TimespanService) { }

  ngOnInit(): void {
  }

  /**
   * Obtém um array com os nomes das colunas dos tempos.
   * Usado para inicializar o array displayedColumns, que será usado no componente tabela do Material.
   * @returns nomes das colunas, usados como referências no componente tabela.
   */

  hideMatrix() {
    this.tableClosed.emit(true);
  }

  /**
   * Atualiza o gráfico das intensidades da chuva dentro do intervalo de tempo do alerta / aviso.
   * @param stationList tem os dados das chuvas medidas pelas estações. 
   * @param RT 
   */
  updateAlertMatrix(stationList: any,RT:boolean){
    this.stationsArray = stationList.stations;
    this.RT = RT;
    this.searchField.allOptions = this.stationsArray.map(s => s.station);
    this.stationsArray.sort( (a,b) =>{
      if(a.station < b.station) { return -1; }
      if(a.station > b.station) { return 1; }
    return 0;
    });

    this.heatmatrixClass = "alertMatrix-show";
    this.props = this.getValueArray(this.stationsArray, RT);
    console.log('props aqui', this.props);
    this.searchField.allOptions = Array.from(
      new Set(
        this.props.map(p => p['Estação'])
      )
    );

    this.loadChart(this.props, RT);
  }

  /**
   * Cria o spec do vega-lite, que tem os dados e a configuração para exibir o gráfico de valores absolutos.
   * 
   * @returns 
   */
   createSpec(props:any[],RT:boolean) {
    let [domain,range] = this.getDomainAndRange(props);
    const spec : VisualizationSpec = {
      "width": 400,
      "title": `Linha do tempo do ${this.alerta.classificacao} entre ${this.alerta.horario_inicial} e ${RT ? this.timespanService.formatDatetime(new Date()) : this.alerta.horario_final}`,
      "data": {
        "values": props,
      },
      "params": [
        {
          "name": "grid",
          "select": {"type": "interval", "encodings": ["x"]},
          "bind": "scales",
        },
      ],
      "mark": {"type":"bar","tooltip": true},
      "encoding": {
        "y": {"field": "Estação", "type": "ordinal"},
        "x": {"field": "Começo", "type": "temporal","scale":{"domain":[this.getDateFromText(this.alerta.horario_inicial).getTime(),RT ? new Date().getTime() : this.getDateFromText(this.alerta.horario_final).getTime()]}},
        "x2": {"field": "Final", "type": "temporal"},
        "tooltip": [{"field": "Começo", "type": "temporal","timeUnit":"yearmonthdatehoursminutes"},{"field": "Final", "type": "temporal","timeUnit":"yearmonthdatehoursminutes"},{"field":"Medida","type":"quantitative"}],
        "color": {"field":"color","type":"nominal","title": "Legenda","scale":{
          "domain": domain,
          "range":range  
        }}
      }
    };
    return spec;
  }


  /**
   * Atualiza o gráfico, exibindo as estações selecionadas no intervalo de datas selecionado pelo usuário.
   */
   async loadChart(props:any[],RT:boolean) {
    // let spec : VisualizationSpec = this.createSpec(element,RT);
    let spec : VisualizationSpec = this.createSpec(props, RT);

    const embedOpts = {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      },
      mode: 'vega-lite' as Mode,
      defaultStyle: true, 
      i18n: {
        PNG_ACTION: "Salvar como PNG",
        SVG_ACTION: "Salvar como SVG",
      }
    };

    await embed('#plotting-area-2', spec, embedOpts);
  }

  /**
   * Cria array das intensidades de chuva registradas pelas estações no intervalo \
   * de tempo.
   * @param element Cada elemento é uma estação e tem uma lista das medições da chuva.
   * @param RT 
   * @returns array das medidas das estações com a categoria da intensidade registrada.
   */
  getValueArray(element:any[],RT:boolean):any[]{
    let returnArray:any[] = [];
    let finaldate : Date;
    if(RT) finaldate = new Date()
    else finaldate = this.getDateFromText(this.alerta.horario_final)
    element.forEach(stationElement=>{
      let first = true,last = false;
      stationElement.rawData.forEach((val:any,index:number)=>{
        let type = "",color = "",value = 0
        if(val.quinzemin > 0 && val.quinzemin < 0.625 ){
          type=stationElement.station;
          color="Fraca";
        }
        else if(val.quinzemin >=0.625 && val.quinzemin < 2.5 ){
          type=stationElement.station;
          color="Moderada";
        }
        else if(val.quinzemin >=2.5 && val.quinzemin < 12.5 ){
          type=stationElement.station;
          color="Forte";
        }
        else if(val.quinzemin >= 12.5 ){
          type=stationElement.station;
          color="Muito forte";
        }
        else{
          type = stationElement.station;
          color=""
        }
        value = val.quinzemin;
        let date1,date2
        date2 = new Date(val.ano,val.mes-1,val.dia,val.hora,val.minuto);
        if (first){
          date1 = this.getDateFromText(this.alerta.horario_inicial);
          if(date2.getTime() > date1.getTime()){
            first = false;
          }
        } else {
          let prev = stationElement.rawData[index-1];
          date1 = new Date(prev.ano,prev.mes-1,prev.dia,prev.hora,prev.minuto);
        }

        if(date2.getTime() > finaldate.getTime()  && !last){
          returnArray.push({"Estação": stationElement.station, "Começo": date1, "Final": finaldate,"color":color,"Medida":"?"})
          last = true;
        }

        if(!first && !last){
          if((date2.getTime() - date1.getTime()) > 900000){
            returnArray.push({"Estação": stationElement.station, "Começo": date1, "Final": new Date(date2.getTime()-900000),"color":"Sem Dados","Medida":0})
            date1 = new Date(date2.getTime()-900000)
          }
          returnArray.push({"Estação": stationElement.station, "Começo": date1, "Final": date2,"color":color,"Medida":value})
        }
      })
      if(returnArray.filter(val=>val["Estação"] == stationElement.station && val["Medida"] > 0).length <= 0){
        returnArray = returnArray.filter(val=> val["Estação"] != stationElement.station);
      }
        
    })
    return returnArray.filter(val=>val.color != "");
  }

  /**
   * Cria um objeto date a partir de uma string YYYY-MM-DD hh:mm
   * @param dateStr data no formato YYYY-MM-DD hh:mm
   * @returns objeto date
   */
  getDateFromText(dateStr:string){
      let day = parseInt(dateStr.slice(0,2));
      let month = parseInt(dateStr.slice(3,5))-1;
      let year = parseInt(dateStr.slice(6,10));
      let hour = parseInt(dateStr.slice(11,13));
      let minute = parseInt(dateStr.slice(14,16));

      return new Date(year,month,day,hour,minute);
  }

  /**
   * Obtém o domínio (nomes das intensidades de chuva registradas) e \
   * o range (cores relacionadas às intensidades).
   * @param props array das medições da chuva com as intensidades.
   * @returns array cujo primeiro elemento é o array dos nomes das intensidades, \
   * e o segundo é o array das cores.
   */
  getDomainAndRange(props:any[]){
    let domain:string[] = [];
    let range:string[] = [];
    Object.keys(this.discreteColors).forEach((val)=>{
      if(props.filter(value=>value.color.toLowerCase() == val).length > 0){
        domain.push(val.charAt(0).toUpperCase()+val.slice(1))
        range.push(this.discreteColors[val])
      }
    })
    if(props.filter(val=>val.color == "Sem Dados").length > 0){
      domain.push("Sem Dados")
      range.push("#808080")
    }
    domain.push("");
    range.push("#FFFFFF")
    return [domain,range];
  }

  headerMouseDown(event: any) {
    this.dragDisabled.emit(false)
  }

  /**
   * 
   * @param event 
   */
  headerMouseUp(event: any) {
    this.dragDisabled.emit(true);
  }

  /**
   * Reage a mudanças no filtro de bairros, solicitando a atualização do gráfico.
   * @param options: nome das estações que podem ser selecionadas no filtro.
   */
   async onSelectedOptionsChanged(options: string[]) {
    this.selectedStations = options;
    let filteredProps = options.length == 0 ? this.props : this.props.filter(p => options.includes(p['Estação']))
    await this.loadChart(filteredProps, this.RT);
  }
}
